import { useState, useRef, useEffect } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Search, Package, Video, Download, Copy, RotateCcw, Trash2, Truck, Clock, FileText, AlertTriangle, MessageSquare, Plus, X, Eye, Upload, Camera } from "lucide-react";
import { FileSpreadsheet } from "lucide-react";

// ═══════════════════════════════════════════════════════════════════
// UTILITIES
// ═══════════════════════════════════════════════════════════════════
function normalizeCodBip(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.includes("@id@>") || trimmed.includes("@Id@>") || trimmed.includes("@ID@>")) {
    const match = trimmed.match(/@(?:id|Id|ID)@>@?([^@,]+)@?/);
    if (match && match[1].trim() !== "") return match[1].trim();
  }
  if (trimmed.includes("^id^Ç^") || trimmed.includes("^Id^Ç^") || trimmed.includes("^ID^Ç^")) {
    const match = trimmed.match(/\^(?:id|Id|ID)\^Ç\^([^^,]+)/);
    if (match && match[1].trim() !== "") return match[1].trim();
  }
  const repeatedMatch = trimmed.match(/^([^,]+),\1(?:,[^,]*)?$/);
  if (repeatedMatch && repeatedMatch[1].trim() !== "") return repeatedMatch[1].trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const id = parsed["id"] ?? parsed["Id"] ?? parsed["ID"];
        if (id !== undefined && id !== null && String(id).trim() !== "") return String(id).trim();
      }
    } catch { /* não é JSON válido */ }
  }
  return trimmed;
}

function formatDate(d: any) {
  if (!d) return "-";
  const date = new Date(d);
  return date.toLocaleDateString("pt-BR") + " " + date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function formatDuration(seconds: number | null) {
  if (!seconds) return "-";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function possuiDadoValido(devolucao: any, campos: string[]) {
  return campos.some((campo) => {
    const valor = devolucao?.[campo];
    return valor !== null && valor !== undefined && String(valor).trim() !== "";
  });
}

function possuiDadosMercadoLivre(devolucao: any) {
  return possuiDadoValido(devolucao, ["mlPedidoId", "mlPackId", "mlShippingIdPedido", "mlClaimId", "mlShippingIdDevol", "mlItensPedido"]);
}

function possuiDadosSincronizados(devolucao: any) {
  return possuiDadosMercadoLivre(devolucao)
    || possuiDadoValido(devolucao, ["notaFiscalProcessadaJadlog", "dacteJadlog", "xmlJadlog", "trackingJadlog", "destinatarioJadlog"])
    || possuiDadoValido(devolucao, ["lojaShopee", "compradorShopee", "pedidoShopee", "rastreioOriginalShopee", "dataEnvioShopee", "dataDevolucaoShopee", "motivoShopee"]);
}

type DevolucoesSortKey = "codigoBip" | "marketplaceNome" | "bipEntradaEm" | "videos" | "outrosArquivos" | "anotacao" | "acaoDevolvido" | "prazo";

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
export default function SearchBox() {
  const { user } = useGoogleAuth();
  const isAdmin = user?.role === "admin";

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">Buscar Pedido MECOM</h1>
      <Tabs defaultValue="buscar">
        <TabsList>
          <TabsTrigger value="buscar"><Search className="w-4 h-4 mr-1" />Buscar Pedido</TabsTrigger>
          <TabsTrigger value="devolucoes"><RotateCcw className="w-4 h-4 mr-1" />Devoluções</TabsTrigger>
        </TabsList>
        <TabsContent value="buscar"><BuscarPedidoTab user={user} isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="devolucoes"><DevolucoesTab user={user} isAdmin={isAdmin} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB: BUSCAR PEDIDO
// ═══════════════════════════════════════════════════════════════════
function BuscarPedidoTab({ user, isAdmin }: { user: any; isAdmin: boolean }) {
  const [codigoBip, setCodigoBip] = useState("");
  const [searchCode, setSearchCode] = useState("");
  const [emitenteId, setEmitenteId] = useState<string>("__all__");
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: emitentes } = trpc.emitentes.list.useQuery();

  const emitenteIdNum = emitenteId !== "__all__" ? Number(emitenteId) : undefined;
  const { data: pedido, isLoading, error } = trpc.searchBox.search.useQuery(
    { codigoBip: searchCode, emitenteId: emitenteIdNum },
    { enabled: !!searchCode }
  );

  const handleSearch = () => {
    const normalized = normalizeCodBip(codigoBip);
    if (!normalized) {
      toast.error("Digite um código para buscar");
      return;
    }
    setSearchCode(normalized);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  const copyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success("Link copiado!");
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex flex-wrap gap-2 items-center">
        {isAdmin && (
          <Select value={emitenteId} onValueChange={setEmitenteId}>
            <SelectTrigger className="w-[200px]"><SelectValue placeholder="Emitente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos</SelectItem>
              {(emitentes as any[] || []).map((e: any) => (
                <SelectItem key={e.id} value={String(e.id)}>{e.razaoSocial}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Input
          ref={inputRef}
          value={codigoBip}
          onChange={(e) => setCodigoBip(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Código do pedido (bip ou digitado)"
          className="w-[300px]"
        />
        <Button onClick={handleSearch} disabled={isLoading}>
          <Search className="w-4 h-4 mr-1" />{isLoading ? "Buscando..." : "Buscar"}
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700 text-sm">
          Erro: {error.message}
        </div>
      )}

      {searchCode && !isLoading && !pedido && !error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-yellow-700 text-sm">
          Nenhum pedido encontrado com o código "{searchCode}".
        </div>
      )}

      {pedido && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Pedido: {pedido.codigoBip}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <span className="text-sm text-muted-foreground">Status</span>
                <p className="font-medium capitalize">{pedido.status}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Loja</span>
                <p className="font-medium">{pedido.marketplaceNome || "-"}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Envio</span>
                <p className="font-medium">{pedido.envioNome || "-"}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Transportadora</span>
                <p className="font-medium">{pedido.transporteNome || "-"}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Ponto Operacional</span>
                <p className="font-medium">{pedido.pontoOperacionalTipo || "-"}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Data Entrada</span>
                <p className="font-medium">{formatDate(pedido.bipEntradaEm)}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Data Gravação</span>
                <p className="font-medium">{formatDate(pedido.bipGravacaoEm)}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Data Expedição</span>
                <p className="font-medium">{formatDate(pedido.bipExpedicaoEm)}</p>
              </div>
              {pedido.bipDevolucaoEm && (
                <div>
                  <span className="text-sm text-muted-foreground">Data Devolução</span>
                  <p className="font-medium">{formatDate(pedido.bipDevolucaoEm)}</p>
                </div>
              )}
              {pedido.gravacaoDuracao && (
                <div>
                  <span className="text-sm text-muted-foreground">Duração Gravação</span>
                  <p className="font-medium">{formatDuration(pedido.gravacaoDuracao)}</p>
                </div>
              )}
              {pedido.devolucaoDuracao && (
                <div>
                  <span className="text-sm text-muted-foreground">Duração Devolução</span>
                  <p className="font-medium">{formatDuration(pedido.devolucaoDuracao)}</p>
                </div>
              )}
            </div>

            {/* Vídeo de Gravação */}
            {pedido.videoGravacaoUrl && (
              <div className="space-y-2">
                <h3 className="font-semibold flex items-center gap-1"><Video className="w-4 h-4" /> Gravação</h3>
                <video src={pedido.videoGravacaoUrl} controls className="w-full max-w-lg rounded border" />
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => copyLink(pedido.videoGravacaoUrl)}>
                    <Copy className="w-3 h-3 mr-1" />Copiar Link
                  </Button>
                  <a href={pedido.videoGravacaoUrl} download target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm"><Download className="w-3 h-3 mr-1" />Download</Button>
                  </a>
                </div>
              </div>
            )}

            {/* Vídeo de Devolução */}
            {pedido.videoDevolucaoUrl && (
              <div className="space-y-2">
                <h3 className="font-semibold flex items-center gap-1"><Video className="w-4 h-4" /> Devolução</h3>
                <video src={pedido.videoDevolucaoUrl} controls className="w-full max-w-lg rounded border" />
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => copyLink(pedido.videoDevolucaoUrl)}>
                    <Copy className="w-3 h-3 mr-1" />Copiar Link
                  </Button>
                  <a href={pedido.videoDevolucaoUrl} download target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm"><Download className="w-3 h-3 mr-1" />Download</Button>
                  </a>
                </div>
              </div>
            )}

            {/* Romaneio */}
            <RomaneioSection codigoBip={pedido.codigoBip} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ROMANEIO SECTION
// ═══════════════════════════════════════════════════════════════════
function RomaneioSection({ codigoBip }: { codigoBip: string }) {
  const { data: romaneio } = trpc.searchBox.getRomaneio.useQuery({ codigoBip }, { enabled: !!codigoBip });
  if (!romaneio) return null;
  return (
    <div className="space-y-2">
      <h3 className="font-semibold flex items-center gap-1"><FileText className="w-4 h-4" /> Romaneio</h3>
      <div className="text-sm text-muted-foreground">
        <p>Motorista: <span className="font-medium text-foreground">{(romaneio as any).motoristaNome}</span></p>
        <p>RG: <span className="font-medium text-foreground">{(romaneio as any).motoristaRg}</span></p>
        <p>Placa: <span className="font-medium text-foreground">{(romaneio as any).placaVeiculo}</span></p>
        <p>Data: <span className="font-medium text-foreground">{new Date((romaneio as any).criadoEm).toLocaleString('pt-BR')}</span></p>
      </div>
      {(romaneio as any).documentoPdfPath && (
        <a href={(romaneio as any).documentoPdfPath} target="_blank" rel="noopener noreferrer">
          <Button variant="outline" size="sm"><FileText className="w-3 h-3 mr-1" />Ver Romaneio PDF</Button>
        </a>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB: DEVOLUÇÕES
// ═══════════════════════════════════════════════════════════════════
function DevolucoesTab({ user, isAdmin }: { user: any; isAdmin: boolean }) {
  const [emitenteId, setEmitenteId] = useState<string>("__all__");
  const [search, setSearch] = useState("");
  const [acaoFilter, setAcaoFilter] = useState<string>("__all__");
  const [videoCorrompidoFilter, setVideoCorrompidoFilter] = useState<string>("__all__");
  const [marketplaceFilter, setMarketplaceFilter] = useState<string>("__all__");
  const [dataEntradaInicio, setDataEntradaInicio] = useState<string>("");
  const [dataEntradaFim, setDataEntradaFim] = useState<string>("");
  const [sortKey, setSortKey] = useState<DevolucoesSortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPedido, setModalPedido] = useState<any>(null);
  const [modalAcao, setModalAcao] = useState<"estoque" | "descarte" | "retirada">("estoque");
  const [formItens, setFormItens] = useState<Array<{ produtoCod: string; produtoDescricao: string; quantidade: string; codValido: boolean | null; trocarEmbalagem: boolean }>>([{ produtoCod: "", produtoDescricao: "", quantidade: "1", codValido: null, trocarEmbalagem: false }]);
  const [validatingIndex, setValidatingIndex] = useState<number | null>(null);
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [logPedidoId, setLogPedidoId] = useState<number | null>(null);
  const [anotacaoModalOpen, setAnotacaoModalOpen] = useState(false);
  const [anotacaoPedidoId, setAnotacaoPedidoId] = useState<number | null>(null);
  const [anotacaoTexto, setAnotacaoTexto] = useState("");
  const [fotosMap, setFotosMap] = useState<Record<string, any>>({});
  const [fotosModalOpen, setFotosModalOpen] = useState(false);
  const [fotosModalData, setFotosModalData] = useState<any>(null);
  const [fotosInternasMap, setFotosInternasMap] = useState<Record<string, string[]>>({});
  const [uploadFotosOpen, setUploadFotosOpen] = useState(false);
  const [uploadFotosFiles, setUploadFotosFiles] = useState<File[]>([]);
  const [uploadFotosProgress, setUploadFotosProgress] = useState<string>("");
  const [uploadFotosResultados, setUploadFotosResultados] = useState<any[]>([]);
  const [uploadFotosLoading, setUploadFotosLoading] = useState(false);
  const [fotoGuiadaOpen, setFotoGuiadaOpen] = useState(false);
  const [fotoGuiadaEtapa, setFotoGuiadaEtapa] = useState<1 | 2 | 3>(1);
  const [fotoGuiadaDevolucao, setFotoGuiadaDevolucao] = useState<any>(null);
  const [fotoGuiadaDevolucoesEncontradas, setFotoGuiadaDevolucoesEncontradas] = useState<any[]>([]);
  const [fotoGuiadaEtiquetaFile, setFotoGuiadaEtiquetaFile] = useState<File | null>(null);
  const [fotoGuiadaLoading, setFotoGuiadaLoading] = useState(false);
  const fotoGuiadaInputRef = useRef<HTMLInputElement>(null);
  const [mlModalOpen, setMlModalOpen] = useState(false);
  const [mlModalData, setMlModalData] = useState<any>(null);
  const [jadlogModalOpen, setJadlogModalOpen] = useState(false);
  const [jadlogModalData, setJadlogModalData] = useState<any>(null);
  const [shopeeModalOpen, setShopeeModalOpen] = useState(false);
  const [shopeeModalData, setShopeeModalData] = useState<any>(null);
  const shopeeFileInputRef = useRef<HTMLInputElement>(null);
  const [importacaoShopeeRunning, setImportacaoShopeeRunning] = useState(false);
  const [importacaoShopeeTotal, setImportacaoShopeeTotal] = useState(0);
  const [importacaoShopeeProcessadas, setImportacaoShopeeProcessadas] = useState(0);

  // Busca fotos internas para os pedidos listados
  const buscarFotosInternas = async (codigos: string[]) => {
    const novoMap: Record<string, string[]> = {};
    await Promise.all(codigos.map(async (cod) => {
      try {
        const res = await fetch(`/api/devolucao-fotos?codigoBip=${encodeURIComponent(cod)}`, { credentials: 'include' });
        if (res.ok) {
          const rows: any[] = await res.json();
          if (rows.length > 0) novoMap[cod] = rows.map((r: any) => r.arquivoUrl);
        }
      } catch (_) {}
    }));
    setFotosInternasMap(prev => ({ ...prev, ...novoMap }));
  };

  useEffect(() => {
    fetch('https://mapeamento.live/devolucoes-fotos.json')
      .then(r => r.json())
      .then((arr: any[]) => {
        const map: Record<string, any> = {};
        arr.forEach(item => { map[item.cod] = item; });
        setFotosMap(map);
      })
      .catch(() => {});
  }, []);

  const { data: emitentes } = trpc.emitentes.list.useQuery();
  const { data: marketplaces } = trpc.mecom.marketplaces.list.useQuery();
  const emitenteIdNum = emitenteId !== "__all__" ? Number(emitenteId) : undefined;

  const { data: devolucoes, error: devolucoesError, refetch } = trpc.searchBox.listDevolucoes.useQuery({
    emitenteId: emitenteIdNum,
    search: search || undefined,
    acaoDevolvido: acaoFilter !== "__all__" ? acaoFilter : undefined,
    videoCorrompido: videoCorrompidoFilter === "corrompido" ? true : undefined,
    marketplaceId: marketplaceFilter !== "__all__" ? Number(marketplaceFilter) : undefined,
    dataEntradaInicio: dataEntradaInicio || undefined,
    dataEntradaFim: dataEntradaFim || undefined,
  });
  console.log("[SearchBox] listDevolucoes", { devolucoes, devolucoesError });

  const [syncMlModalOpen, setSyncMlModalOpen] = useState(false);
  const [syncMlTotal, setSyncMlTotal] = useState(0);
  const [syncMlProcessados, setSyncMlProcessados] = useState(0);
  const [syncMlErros, setSyncMlErros] = useState(0);
  const [syncMlErrosList, setSyncMlErrosList] = useState<string[]>([]);
  const [syncMlRunning, setSyncMlRunning] = useState(false);
  const syncMlCancelRef = useRef(false);
  const { refetch: refetchSyncMlCount } = trpc.searchBox.syncMlDadosCount.useQuery(undefined, { enabled: false });
  const syncMlPendentesMutation = trpc.searchBox.syncMlDadosPendentes.useMutation();
  const [syncJadlogModalOpen, setSyncJadlogModalOpen] = useState(false);
  const [syncJadlogTotal, setSyncJadlogTotal] = useState(0);
  const [syncJadlogProcessados, setSyncJadlogProcessados] = useState(0);
  const [syncJadlogErros, setSyncJadlogErros] = useState(0);
  const [syncJadlogErrosList, setSyncJadlogErrosList] = useState<string[]>([]);
  const [syncJadlogRunning, setSyncJadlogRunning] = useState(false);
  const syncJadlogCancelRef = useRef(false);
  const { refetch: refetchSyncJadlogCount } = trpc.searchBox.syncJadlogDadosCount.useQuery(undefined, { enabled: false });
  const syncJadlogPendentesMutation = trpc.searchBox.syncJadlogDadosPendentes.useMutation();
  const syncJadlogDadosBipMutation = trpc.searchBox.syncJadlogDadosBip.useMutation({
    onSuccess: () => {
      toast.success('Dados Jadlog reconsultados.');
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });
  const importarPlanilhaShopeeMutation = trpc.searchBox.importarPlanilhaShopee.useMutation({
    onError: (err) => toast.error(err.message),
  });

  const startSyncMl = async () => {
    syncMlCancelRef.current = false;
    setSyncMlProcessados(0);
    setSyncMlErros(0);
    setSyncMlErrosList([]);
    setSyncMlRunning(true);
    setSyncMlModalOpen(true);
    const countResult = await refetchSyncMlCount();
    const total = countResult.data?.total ?? 0;
    const snapshotMaxId = countResult.data?.snapshotMaxId ?? 0;
    setSyncMlTotal(total);
    if (total === 0 || snapshotMaxId === 0) { setSyncMlRunning(false); return; }
    let processadosAcc = 0;
    let errosAcc = 0;
    let lastId = 0;
    const errosListAcc: string[] = [];
    const LOTE = 20;
    while (!syncMlCancelRef.current) {
      try {
        const result = await syncMlPendentesMutation.mutateAsync({ pageSize: LOTE, lastId, snapshotMaxId });
        processadosAcc += result.processados;
        errosAcc += result.erros;
        lastId = result.lastId;
        errosListAcc.push(...(result.errosList ?? []));
        setSyncMlProcessados(processadosAcc);
        setSyncMlErros(errosAcc);
        setSyncMlErrosList([...errosListAcc]);
        if (!result.hasMore) break;
      } catch (err: any) {
        toast.error('Erro no lote: ' + err.message);
        break;
      }
      await new Promise(r => setTimeout(r, 500));
    }
    setSyncMlRunning(false);
    refetch();
  };

  const startSyncJadlog = async () => {
    syncJadlogCancelRef.current = false;
    setSyncJadlogProcessados(0);
    setSyncJadlogErros(0);
    setSyncJadlogErrosList([]);
    setSyncJadlogRunning(true);
    setSyncJadlogModalOpen(true);
    const countResult = await refetchSyncJadlogCount();
    const total = countResult.data?.total ?? 0;
    const snapshotMaxId = countResult.data?.snapshotMaxId ?? 0;
    setSyncJadlogTotal(total);
    if (total === 0 || snapshotMaxId === 0) { setSyncJadlogRunning(false); return; }
    let processadosAcc = 0;
    let errosAcc = 0;
    let lastId = 0;
    const errosListAcc: string[] = [];
    const LOTE = 5;
    while (!syncJadlogCancelRef.current) {
      try {
        const result = await syncJadlogPendentesMutation.mutateAsync({ pageSize: LOTE, lastId, snapshotMaxId });
        processadosAcc += result.processados;
        errosAcc += result.erros;
        lastId = result.lastId;
        errosListAcc.push(...(result.errosList ?? []));
        setSyncJadlogProcessados(processadosAcc);
        setSyncJadlogErros(errosAcc);
        setSyncJadlogErrosList([...errosListAcc]);
        if (!result.hasMore) break;
      } catch (err: any) {
        toast.error('Erro no lote: ' + err.message);
        break;
      }
      await new Promise(r => setTimeout(r, 500));
    }
    setSyncJadlogRunning(false);
    refetch();
  };

  const importarPlanilhaShopee = (arquivo: File) => {
    const leitor = new FileReader();
    leitor.onload = async () => {
      const base64 = String(leitor.result || '').split(',')[1];
      if (!base64) {
        toast.error('Não foi possível ler a planilha Shopee.');
        return;
      }
      setImportacaoShopeeRunning(true);
      setImportacaoShopeeTotal(0);
      setImportacaoShopeeProcessadas(0);
      let offset = 0;
      let atualizados = 0;
      let naoLocalizados = 0;
      let temMais = true;
      try {
        const inicio = await importarPlanilhaShopeeMutation.mutateAsync({ arquivoBase64: base64, offset, limite: 0 });
        offset = inicio.proximoOffset;
        temMais = inicio.temMais;
        setImportacaoShopeeTotal(inicio.linhasLidas);
        while (temMais) {
          const resultado = await importarPlanilhaShopeeMutation.mutateAsync({ arquivoBase64: base64, offset, limite: 25 });
          offset = resultado.proximoOffset;
          atualizados += resultado.atualizados;
          naoLocalizados += resultado.naoLocalizados;
          temMais = resultado.temMais;
          setImportacaoShopeeTotal(resultado.linhasLidas);
          setImportacaoShopeeProcessadas(offset);
        }
        toast.success(`${atualizados} devolução(ões) atualizada(s); ${naoLocalizados} rastreio(s) não localizado(s).`);
        refetch();
      } catch {
        // Erro já apresentado pelo onError da mutation.
      } finally {
        setImportacaoShopeeRunning(false);
      }
    };
    leitor.onerror = () => toast.error('Não foi possível ler a planilha Shopee.');
    leitor.readAsDataURL(arquivo);
  };

  const { data: logEntries } = trpc.searchBox.listLog.useQuery(
    { pedidoId: logPedidoId! },
    { enabled: !!logPedidoId }
  );

  const { data: anotacoes, refetch: refetchAnotacoes } = trpc.searchBox.listAnotacoes.useQuery(
    { pedidoId: anotacaoPedidoId! },
    { enabled: !!anotacaoPedidoId }
  );



  useEffect(() => {
    if (devolucoes && devolucoes.length > 0) {
      const codigos = (devolucoes as any[]).map((d: any) => d.codigoBip).filter(Boolean);
      buscarFotosInternas(codigos);
    }
  }, [devolucoes]);

  const acaoMutation = trpc.searchBox.acaoDevolucao.useMutation({
    onSuccess: () => {
      toast.success("Ação registrada com sucesso!");
      setModalOpen(false);
      setSelectedIds([]);
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const deletePedidoMutation = trpc.mecom.deletePedido.useMutation({
    onSuccess: () => {
      toast.success("Pedido excluído!");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const deletePedidosMassaMutation = trpc.mecom.deletePedidosMassa.useMutation({
    onSuccess: (resultado) => {
      toast.success(`${resultado.excluidos} pedido(s) excluído(s)!`);
      setSelectedIds([]);
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const addAnotacaoMutation = trpc.searchBox.addAnotacao.useMutation({
    onSuccess: () => {
      toast.success("Anotação adicionada!");
      setAnotacaoTexto("");
      refetchAnotacoes();
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const openAcaoModal = (pedido: any, acao: "estoque" | "descarte" | "retirada") => {
    setModalPedido(pedido);
    setModalAcao(acao);
    setFormItens([{ produtoCod: "", produtoDescricao: "", quantidade: "1", codValido: null, trocarEmbalagem: false }]);
    setModalOpen(true);
  };

  // Helpers multi-itens
  const addItem = () => setFormItens((prev) => [...prev, { produtoCod: "", produtoDescricao: "", quantidade: "1", codValido: null, trocarEmbalagem: false }]);
  const removeItem = (index: number) => setFormItens((prev) => prev.filter((_, i) => i !== index));
  const updateItem = (index: number, field: string, value: any) => {
    setFormItens((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleValidateProdutoCod = async (index: number, cod: string) => {
    if (!cod.trim()) return;
    setValidatingIndex(index);
    try {
      const res = await fetch(`/api/trpc/products.getByCod?input=${encodeURIComponent(JSON.stringify({ json: { cod, emitenteId: modalPedido?.emitenteId } }))}`, { credentials: 'include' });
      const json = await res.json();
      const product = json?.result?.data?.json ?? json?.result?.data;
      if (product && product.descricao) {
        updateItem(index, "produtoDescricao", product.descricao);
        updateItem(index, "codValido", true);
      } else {
        updateItem(index, "codValido", false);
      }
    } catch {
      updateItem(index, "codValido", false);
    }
    setValidatingIndex(null);
  };

  const handleAcaoSubmit = () => {
    if (!modalPedido) return;
    if (modalAcao === "estoque") {
      const itensValidos = formItens.filter((i) => i.produtoCod.trim() && Number(i.quantidade) > 0);
      if (itensValidos.length === 0) {
        toast.error("Adicione pelo menos um item com código e quantidade");
        return;
      }
      const itensInvalidos = itensValidos.filter((i) => i.codValido !== true);
      if (itensInvalidos.length > 0) {
        toast.error(`Códigos não validados: ${itensInvalidos.map(i => i.produtoCod).join(", ")}. Pressione Enter no campo para validar.`);
        return;
      }
      acaoMutation.mutate({
        pedidoId: modalPedido.id,
        acao: modalAcao,
        itens: itensValidos.map((i) => ({ produtoCod: i.produtoCod, produtoDescricao: i.produtoDescricao, quantidade: Number(i.quantidade), trocarEmbalagem: i.trocarEmbalagem })),
        emitenteId: modalPedido.emitenteId,
      });
    } else {
      acaoMutation.mutate({
        pedidoId: modalPedido.id,
        acao: modalAcao,
        emitenteId: modalPedido.emitenteId,
      });
    }
  };

  const handleBulkAction = (acao: "descarte" | "retirada") => {
    if (selectedIds.length === 0) {
      toast.error("Selecione pelo menos um pedido");
      return;
    }
    for (const id of selectedIds) {
      acaoMutation.mutate({
        pedidoId: id,
        acao,
        emitenteId: emitenteIdNum,
      });
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    const pendentes = (devolucoes || []).filter((d: any) => !d.acaoDevolvido);
    if (selectedIds.length === pendentes.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pendentes.map((d: any) => d.id));
    }
  };

  const getPrazoInfo = (pedido: any) => {
    if (!pedido.bipDevolucaoEm) return null;
    const devDate = new Date(pedido.bipDevolucaoEm);
    const prazo = new Date(devDate.getTime() + 7 * 24 * 60 * 60 * 1000);
    const agora = new Date();
    const diasRestantes = Math.ceil((prazo.getTime() - agora.getTime()) / (1000 * 60 * 60 * 24));
    return { prazo, diasRestantes };
  };

  const handleSort = (key: DevolucoesSortKey) => {
    if (sortKey === key) {
      setSortDirection((direction) => direction === "asc" ? "desc" : "asc");
      return;
    }
    setSortKey(key);
    setSortDirection("asc");
  };

  const renderSortableHeader = (label: string, key: DevolucoesSortKey) => (
    <button type="button" className="inline-flex items-center gap-1 font-semibold" onClick={() => handleSort(key)}>
      {label}<span>{sortKey === key ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}</span>
    </button>
  );

  const devolucoesOrdenadas = sortKey ? [...((devolucoes || []) as any[])].sort((a, b) => {
    const getValor = (item: any) => {
      if (sortKey === "codigoBip") return String(item.codigoBip || "").toLocaleLowerCase("pt-BR");
      if (sortKey === "marketplaceNome") return String(item.marketplaceNome || "").toLocaleLowerCase("pt-BR");
      if (sortKey === "bipEntradaEm") return item.bipEntradaEm ? new Date(item.bipEntradaEm).getTime() : 0;
      if (sortKey === "videos") return Number(Boolean(item.videoGravacaoUrl)) + Number(Boolean(item.videoDevolucaoUrl));
      if (sortKey === "outrosArquivos") return Number(Boolean(fotosMap[item.codigoBip])) + (fotosInternasMap[item.codigoBip]?.length || 0) + Number(Boolean(item.mlClaimId || item.mlPedidoId || item.mlErro));
      if (sortKey === "anotacao") return String(item.anotacao || "").toLocaleLowerCase("pt-BR");
      if (sortKey === "acaoDevolvido") return String(item.acaoDevolvido || "pendente").toLocaleLowerCase("pt-BR");
      return item.acaoDevolvido || !item.bipDevolucaoEm ? 0 : new Date(item.bipDevolucaoEm).getTime() + 7 * 24 * 60 * 60 * 1000;
    };
    const valorA = getValor(a);
    const valorB = getValor(b);
    const resultado = typeof valorA === "number" && typeof valorB === "number"
      ? valorA - valorB
      : String(valorA).localeCompare(String(valorB), "pt-BR", { numeric: true, sensitivity: "base" });
    return sortDirection === "asc" ? resultado : -resultado;
  }) : ((devolucoes || []) as any[]);

  const handleDeletePedido = (pedido: any) => {
    if (!window.confirm(`Excluir definitivamente o pedido ${pedido.codigoBip} do MECOM?`)) return;
    deletePedidoMutation.mutate({ id: pedido.id });
  };

  const handleDeletePedidosMassa = () => {
    if (!window.confirm(`Excluir definitivamente ${selectedIds.length} pedido(s) do MECOM?`)) return;
    deletePedidosMassaMutation.mutate({ ids: selectedIds });
  };

  const abrirFotoGuiada = () => {
    setFotoGuiadaEtapa(1);
    setFotoGuiadaDevolucao(null);
    setFotoGuiadaDevolucoesEncontradas([]);
    setFotoGuiadaEtiquetaFile(null);
    setFotoGuiadaOpen(true);
  };

  const fecharFotoGuiada = () => {
    if (fotoGuiadaLoading) return;
    setFotoGuiadaOpen(false);
  };

  const reduzirFotoGuiada = (file: File): Promise<File> => new Promise((resolve, reject) => {
    const image = new Image();
    const imageUrl = URL.createObjectURL(file);
    image.onload = () => {
      const maiorLado = Math.max(image.naturalWidth, image.naturalHeight);
      const escala = Math.min(1, 1600 / maiorLado);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.naturalWidth * escala));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * escala));
      const contexto = canvas.getContext('2d');
      if (!contexto) {
        URL.revokeObjectURL(imageUrl);
        reject(new Error('Não foi possível preparar a foto'));
        return;
      }
      contexto.drawImage(image, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(imageUrl);
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Não foi possível reduzir a foto'));
          return;
        }
        const nome = file.name.replace(/\.[^.]+$/, '') || 'foto-guiada';
        resolve(new File([blob], `${nome}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified }));
      }, 'image/jpeg', 0.82);
    };
    image.onerror = () => {
      URL.revokeObjectURL(imageUrl);
      reject(new Error('Não foi possível carregar a foto'));
    };
    image.src = imageUrl;
  });

  const salvarEtiquetaSelecionada = async (devolucao: any) => {
    if (!fotoGuiadaEtiquetaFile) {
      toast.error('Tire uma nova foto da etiqueta antes de selecionar a devolução');
      return;
    }
    setFotoGuiadaLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', fotoGuiadaEtiquetaFile);
      formData.append('codigoBip', devolucao.codigoBip);
      const res = await fetch('/api/devolucao-fotos/upload-produto', { method: 'POST', body: formData, credentials: 'include' });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.erro || data.error || 'Não foi possível salvar a foto da etiqueta');
        return;
      }
      await buscarFotosInternas([devolucao.codigoBip]);
      setFotoGuiadaEtiquetaFile(null);
      setFotoGuiadaDevolucao(devolucao);
      setFotoGuiadaEtapa(2);
      if (data.duplicado) toast.info('Etiqueta já cadastrada (duplicata).');
      else toast.success('Etiqueta salva. Tire a primeira foto do produto.');
    } catch (error: any) {
      toast.error('Erro: ' + error.message);
    } finally {
      setFotoGuiadaLoading(false);
    }
  };

  const enviarFotoGuiada = async (file: File) => {
    setFotoGuiadaLoading(true);
    try {
      const formData = new FormData();

      if (fotoGuiadaEtapa === 1) {
        formData.append('file', file);
        setFotoGuiadaDevolucao(null);
        setFotoGuiadaDevolucoesEncontradas([]);
        setFotoGuiadaEtiquetaFile(null);
        const res = await fetch('/api/devolucao-fotos/validar-etiqueta', { method: 'POST', body: formData, credentials: 'include' });
        const data = await res.json();
        if (!res.ok) {
          toast.error(data.erro || data.error || 'Não foi possível validar a etiqueta');
          return;
        }

        const encontradas = data.devolucoesEncontradas || [];
        setFotoGuiadaDevolucoesEncontradas(encontradas);
        if (encontradas.length === 1) {
          await buscarFotosInternas([encontradas[0].codigoBip]);
          setFotoGuiadaDevolucao(encontradas[0]);
          setFotoGuiadaEtapa(2);
          if (data.etiquetaDuplicada) toast.info('Etiqueta já cadastrada (duplicata).');
          else toast.success('Etiqueta salva. Tire a primeira foto do produto.');
        } else {
          setFotoGuiadaEtiquetaFile(file);
          toast.info('Selecione a devolução identificada na etiqueta para salvar a foto.');
        }
        return;
      }

      if (!fotoGuiadaDevolucao?.codigoBip) {
        toast.error('Valide e selecione uma devolução antes de fotografar o produto');
        return;
      }

      const fotoReduzida = await reduzirFotoGuiada(file);
      formData.append('file', fotoReduzida);
      formData.append('codigoBip', fotoGuiadaDevolucao.codigoBip);
      const res = await fetch('/api/devolucao-fotos/upload-produto', { method: 'POST', body: formData, credentials: 'include' });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.erro || data.error || 'Não foi possível salvar a foto do produto');
        return;
      }

      await buscarFotosInternas([fotoGuiadaDevolucao.codigoBip]);
      if (data.duplicado) toast.info('Foto já cadastrada (duplicata).');
      else toast.success(`Foto do produto ${fotoGuiadaEtapa - 1} salva.`);

      if (fotoGuiadaEtapa === 3) {
        setFotoGuiadaEtapa(1);
        setFotoGuiadaDevolucao(null);
        setFotoGuiadaDevolucoesEncontradas([]);
        toast.success('Duas fotos salvas. Pronto para validar a próxima etiqueta.');
      } else {
        setFotoGuiadaEtapa((fotoGuiadaEtapa + 1) as 2 | 3);
      }
    } catch (error: any) {
      toast.error('Erro: ' + error.message);
    } finally {
      setFotoGuiadaLoading(false);
    }
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex flex-wrap gap-2 items-center">
        {isAdmin && (
          <Select value={emitenteId} onValueChange={setEmitenteId}>
            <SelectTrigger className="w-[200px]"><SelectValue placeholder="Emitente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos</SelectItem>
              {(emitentes as any[] || []).map((e: any) => (
                <SelectItem key={e.id} value={String(e.id)}>{e.razaoSocial}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por código..."
          className="w-[200px]"
        />
        <Select value={acaoFilter} onValueChange={setAcaoFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Filtrar ação" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todas</SelectItem>
            <SelectItem value="pendente">Pendente</SelectItem>
            <SelectItem value="estoque">Estoque</SelectItem>
            <SelectItem value="descarte">Descarte</SelectItem>
            <SelectItem value="retirada">Retirada</SelectItem>
          </SelectContent>
        </Select>
        <Select value={videoCorrompidoFilter} onValueChange={setVideoCorrompidoFilter}>
          <SelectTrigger className="w-[210px]"><SelectValue placeholder="Filtrar vídeos" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos os vídeos</SelectItem>
            <SelectItem value="corrompido">Vídeo 0:00 / corrompido</SelectItem>
          </SelectContent>
        </Select>
        <Select value={marketplaceFilter} onValueChange={setMarketplaceFilter}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder="Loja" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todas as lojas</SelectItem>
            {(marketplaces as any[] || []).map((m: any) => (
              <SelectItem key={m.id} value={String(m.id)}>{m.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="date"
          value={dataEntradaInicio}
          onChange={(e) => setDataEntradaInicio(e.target.value)}
          className="w-[150px]"
          title="Data entrada — de"
        />
        <Input
          type="date"
          value={dataEntradaFim}
          onChange={(e) => setDataEntradaFim(e.target.value)}
          className="w-[150px]"
          title="Data entrada — até"
        />
        {isAdmin && (
          <Button size="sm" variant="outline" onClick={() => { setUploadFotosFiles([]); setUploadFotosResultados([]); setUploadFotosProgress(""); setUploadFotosOpen(true); }}>
            <Upload className="w-3 h-3 mr-1" />Fotos Internas
          </Button>
        )}
        {isAdmin && (
          <Button size="sm" variant="outline" disabled={syncMlRunning} onClick={startSyncMl}>
            <Package className="w-3 h-3 mr-1" />{syncMlRunning ? 'Sincronizando...' : 'Sincronizar ML Pendentes'}
          </Button>
        )}
        <Button size="sm" variant="outline" disabled={syncJadlogRunning} onClick={startSyncJadlog}>
          <RotateCcw className="w-3 h-3 mr-1" />{syncJadlogRunning ? 'Reprocessando...' : 'Reprocessar Jadlog Pendentes'}
        </Button>
        <Button size="sm" variant="outline" disabled={importacaoShopeeRunning} onClick={() => shopeeFileInputRef.current?.click()}>
          <FileSpreadsheet className="w-3 h-3 mr-1" />{importacaoShopeeRunning ? 'Importando...' : 'Importar planilha'}
        </Button>
        <input
          ref={shopeeFileInputRef}
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          className="hidden"
          disabled={importacaoShopeeRunning}
          onChange={(e) => {
            const arquivo = e.target.files?.[0];
            e.target.value = '';
            if (arquivo) importarPlanilhaShopee(arquivo);
          }}
        />
        {importacaoShopeeRunning && (
          <div className="w-[220px]">
            <div className="mb-1 text-xs text-muted-foreground">
              {importacaoShopeeTotal > 0 ? `Importando ${importacaoShopeeProcessadas} de ${importacaoShopeeTotal}` : 'Importando...'}
            </div>
            <div className="h-2 w-full rounded-full bg-gray-200">
              <div
                className="h-2 rounded-full bg-orange-500 transition-all"
                style={{ width: `${importacaoShopeeTotal > 0 ? Math.min(100, Math.round((importacaoShopeeProcessadas / importacaoShopeeTotal) * 100)) : 0}%` }}
              />
            </div>
          </div>
        )}
        {syncMlModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { if (!syncMlRunning) setSyncMlModalOpen(false); }}>
            <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-bold mb-4">Sincronização ML Pendentes</h3>
              <div className="mb-3 text-sm text-muted-foreground">
                {syncMlTotal > 0 ? `${syncMlProcessados + syncMlErros} de ${syncMlTotal} processados` : 'Calculando...'}
              </div>
              {syncMlTotal > 0 && (
                <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
                  <div
                    className="bg-blue-500 h-3 rounded-full transition-all"
                    style={{ width: `${Math.min(100, Math.round(((syncMlProcessados + syncMlErros) / syncMlTotal) * 100))}%` }}
                  />
                </div>
              )}
              <div className="flex gap-4 text-sm mb-3">
                <span className="text-green-600">Sucesso: {syncMlProcessados}</span>
                <span className="text-red-600">Erros: {syncMlErros}</span>
              </div>
              {syncMlErrosList.length > 0 && (
                <div className="max-h-40 overflow-y-auto border rounded p-2 text-xs text-red-700 bg-red-50 mb-3">
                  {syncMlErrosList.map((e, i) => <div key={i}>{e}</div>)}
                </div>
              )}
              <div className="flex gap-2 justify-end">
                {syncMlRunning && (
                  <Button size="sm" variant="outline" onClick={() => { syncMlCancelRef.current = true; }}>Cancelar</Button>
                )}
                {!syncMlRunning && (
                  <Button size="sm" onClick={() => setSyncMlModalOpen(false)}>Fechar</Button>
                )}
              </div>
            </div>
          </div>
        )}
        {syncJadlogModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { if (!syncJadlogRunning) setSyncJadlogModalOpen(false); }}>
            <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-bold mb-4">Reprocessamento Jadlog Pendente</h3>
              <div className="mb-3 text-sm text-muted-foreground">
                {syncJadlogTotal > 0 ? `${syncJadlogProcessados + syncJadlogErros} de ${syncJadlogTotal} processados` : 'Calculando...'}
              </div>
              {syncJadlogTotal > 0 && (
                <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
                  <div
                    className="bg-blue-500 h-3 rounded-full transition-all"
                    style={{ width: `${Math.min(100, Math.round(((syncJadlogProcessados + syncJadlogErros) / syncJadlogTotal) * 100))}%` }}
                  />
                </div>
              )}
              <div className="flex gap-4 text-sm mb-3">
                <span className="text-green-600">Sucesso: {syncJadlogProcessados}</span>
                <span className="text-red-600">Erros: {syncJadlogErros}</span>
              </div>
              {syncJadlogErrosList.length > 0 && (
                <div className="max-h-40 overflow-y-auto border rounded p-2 text-xs text-red-700 bg-red-50 mb-3">
                  {syncJadlogErrosList.map((e, i) => <div key={i}>{e}</div>)}
                </div>
              )}
              <div className="flex gap-2 justify-end">
                {syncJadlogRunning && (
                  <Button size="sm" variant="outline" onClick={() => { syncJadlogCancelRef.current = true; }}>Cancelar</Button>
                )}
                {!syncJadlogRunning && (
                  <Button size="sm" onClick={() => setSyncJadlogModalOpen(false)}>Fechar</Button>
                )}
              </div>
            </div>
          </div>
        )}
        {isAdmin && (
          <Button size="sm" variant="outline" onClick={abrirFotoGuiada}>
            <Camera className="w-3 h-3 mr-1" />Tirar Fotos de Devolução
          </Button>
        )}
      </div>

      {selectedIds.length > 0 && (
        <div className="flex gap-2 items-center bg-blue-50 border border-blue-200 rounded p-2">
          <span className="text-sm font-medium">{selectedIds.length} selecionado(s)</span>
          <Button size="sm" variant="destructive" onClick={() => handleBulkAction("descarte")}>
            <Trash2 className="w-3 h-3 mr-1" />Descarte em Massa
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleBulkAction("retirada")}>
            <Truck className="w-3 h-3 mr-1" />Retirada em Massa
          </Button>
          {isAdmin && (
            <Button size="sm" variant="destructive" disabled={deletePedidosMassaMutation.isPending} onClick={handleDeletePedidosMassa}>
              <Trash2 className="w-3 h-3 mr-1" />{deletePedidosMassaMutation.isPending ? "Excluindo..." : "Excluir em Massa"}
            </Button>
          )}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm border">
          <thead className="bg-muted">
            <tr>
              <th className="p-2 text-left w-8">
                <input type="checkbox" onChange={toggleAll} checked={selectedIds.length > 0 && selectedIds.length === (devolucoes || []).filter((d: any) => !d.acaoDevolvido).length} />
              </th>
              <th className="p-2 text-left">{renderSortableHeader("Código", "codigoBip")}</th>
              <th className="p-2 text-left">{renderSortableHeader("Loja", "marketplaceNome")}</th>
              <th className="p-2 text-left">{renderSortableHeader("Data Entrada", "bipEntradaEm")}</th>
              <th className="p-2 text-left">{renderSortableHeader("Vídeos", "videos")}</th>
              <th className="p-2 text-left">{renderSortableHeader("Outros Arquivos", "outrosArquivos")}</th>
              <th className="p-2 text-left">{renderSortableHeader("Anotação", "anotacao")}</th>
              <th className="p-2 text-left">{renderSortableHeader("Ação", "acaoDevolvido")}</th>
              <th className="p-2 text-left">{renderSortableHeader("Prazo", "prazo")}</th>
              <th className="p-2 text-left">Ações</th>
            </tr>
          </thead>
          <tbody>
            {devolucoesOrdenadas.map((d: any) => {
              const prazoInfo = getPrazoInfo(d);
              return (
                <tr key={d.id} className="border-t hover:bg-muted/50">
                  <td className="p-2">
                    {!d.acaoDevolvido && (
                      <input type="checkbox" checked={selectedIds.includes(d.id)} onChange={() => toggleSelect(d.id)} />
                    )}
                  </td>
                  <td className="p-2 font-mono text-xs">
                    <span>{d.codigoBip}</span>
                    {possuiDadosSincronizados(d) && <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 font-sans text-[10px] font-medium text-green-700">Sincronizado</span>}
                    {String(d.codigoBip || "").toUpperCase().startsWith("AP") && <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 font-sans text-[10px] font-medium text-green-700">Rastreio correio</span>}
                  </td>
                  <td className="p-2">{d.marketplaceNome || "-"}</td>
                  <td className="p-2">{formatDate(d.bipEntradaEm)}</td>
                  <td className="p-2">
                    <div className="flex gap-1">
                      {d.videoGravacaoUrl && <span className="text-xs bg-green-100 text-green-700 px-1 rounded">Grav</span>}
                      {d.videoDevolucaoUrl && <span className="text-xs bg-orange-100 text-orange-700 px-1 rounded">Dev</span>}
                    </div>
                  </td>
                  <td className="p-2">
                    {(fotosMap[d.codigoBip] || (fotosInternasMap[d.codigoBip] && fotosInternasMap[d.codigoBip].length > 0)) && (
                      <button
                        className="flex items-center justify-center cursor-pointer hover:opacity-70"
                        onClick={() => { setFotosModalData({ ...fotosMap[d.codigoBip], cod: d.codigoBip, _internas: fotosInternasMap[d.codigoBip] || [] }); setFotosModalOpen(true); }}
                        title="Ver outros arquivos"
                      >
                        <Eye className="w-4 h-4 text-blue-600" />
                      </button>
                    )}
                    {possuiDadosMercadoLivre(d) && (
                      <button
                        className="flex items-center justify-center cursor-pointer hover:opacity-70 mt-1"
                        onClick={() => { setMlModalData(d); setMlModalOpen(true); }}
                        title="Ver dados Mercado Livre"
                      >
                        <Package className="w-4 h-4 text-orange-500" />
                      </button>
                    )}
                    {(d.trackingJadlog || d.dacteJadlog || d.xmlJadlog || d.notaFiscalProcessadaJadlog || d.destinatarioJadlog) && (
                      <button
                        className="flex items-center justify-center cursor-pointer hover:opacity-70 mt-1"
                        onClick={() => { setJadlogModalData(d); setJadlogModalOpen(true); }}
                        title="Ver dados Jadlog"
                      >
                        <Truck className="w-4 h-4 text-blue-600" />
                      </button>
                    )}
                    {(d.lojaShopee || d.compradorShopee || d.pedidoShopee || d.rastreioOriginalShopee || d.dataEnvioShopee || d.dataDevolucaoShopee || d.motivoShopee) && (
                      <button
                        className="flex items-center justify-center cursor-pointer hover:opacity-70 mt-1"
                        onClick={() => { setShopeeModalData(d); setShopeeModalOpen(true); }}
                        title="Ver dados Shopee"
                      >
                        <FileSpreadsheet className="w-4 h-4 text-orange-500" />
                      </button>
                    )}
                  </td>
                  <td className="p-2">
                    <button
                      className="flex items-center justify-center cursor-pointer hover:opacity-70"
                      onClick={() => { setAnotacaoPedidoId(d.id); setAnotacaoModalOpen(true); }}
                      title={d.anotacao ? "Tem anotação" : "Sem anotação"}
                    >
                      <MessageSquare className={`w-4 h-4 ${d.anotacao ? "text-blue-600 fill-blue-100" : "text-muted-foreground"}`} />
                    </button>
                  </td>
                  <td className="p-2">
                    {d.acaoDevolvido ? (
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        d.acaoDevolvido === "estoque" ? "bg-blue-100 text-blue-700" :
                        d.acaoDevolvido === "descarte" ? "bg-red-100 text-red-700" :
                        "bg-purple-100 text-purple-700"
                      }`}>
                        {d.acaoDevolvido === "estoque" ? "Estoque" : d.acaoDevolvido === "descarte" ? "Descarte" : "Retirada"}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Pendente</span>
                    )}
                  </td>
                  <td className="p-2">
                    {prazoInfo && !d.acaoDevolvido && (
                      <span className={`text-xs font-medium ${prazoInfo.diasRestantes <= 1 ? "text-red-600" : prazoInfo.diasRestantes <= 3 ? "text-orange-600" : "text-green-600"}`}>
                        {prazoInfo.diasRestantes <= 0 ? "Expirado" : `${prazoInfo.diasRestantes}d`}
                      </span>
                    )}
                  </td>
                  <td className="p-2">
                    {!d.acaoDevolvido && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => openAcaoModal(d, "estoque")}>
                          Estoque
                        </Button>
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs text-red-600" onClick={() => openAcaoModal(d, "descarte")}>
                          Descarte
                        </Button>
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs text-purple-600" onClick={() => openAcaoModal(d, "retirada")}>
                          Retirada
                        </Button>
                      </div>
                    )}
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs mt-1" onClick={() => { setLogPedidoId(d.id); setLogModalOpen(true); }}>
                      <FileText className="w-3 h-3 mr-1" />Log
                    </Button>
                    {isAdmin && String(d.transporteNome || '').replace(/\s+/g, '').toLowerCase() === 'jadlog' && (
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-xs mt-1" disabled={syncJadlogDadosBipMutation.isPending} onClick={() => syncJadlogDadosBipMutation.mutate({ codigoBip: d.codigoBip, pedidoId: d.id })}>
                        <RotateCcw className="w-3 h-3 mr-1" />Reconsultar Jadlog
                      </Button>
                    )}
                    {isAdmin && (
                      <Button size="sm" variant="destructive" className="h-6 px-2 text-xs mt-1" disabled={deletePedidoMutation.isPending} onClick={() => handleDeletePedido(d)}>
                        <Trash2 className="w-3 h-3 mr-1" />Excluir
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
            {(!devolucoes || devolucoes.length === 0) && (
              <tr><td colSpan={10} className="p-4 text-center text-muted-foreground">Nenhuma devolução encontrada.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Ação */}
      {modalOpen && modalPedido && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setModalOpen(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">
              {modalAcao === "estoque" ? "Voltar ao Estoque" : modalAcao === "descarte" ? "Confirmar Descarte" : "Marcar para Retirada"}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">Pedido: <strong>{modalPedido.codigoBip}</strong></p>

            {modalAcao === "estoque" && (
              <div className="space-y-3">
                <p className="text-xs text-amber-600 font-medium">Digite o código do SKU desmembrado, não são aceitos códigos de kit.</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left px-2 py-1">Código</th>
                        <th className="text-left px-2 py-1">Descrição</th>
                        <th className="text-left px-2 py-1 w-20">Qtd</th>
                        <th className="text-left px-2 py-1">Trocar embalagem</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formItens.map((item, index) => (
                        <tr key={index} className="border-b">
                          <td className="px-2 py-1">
                            <Input
                              value={item.produtoCod}
                              onChange={(e) => { updateItem(index, "produtoCod", e.target.value); updateItem(index, "codValido", null); updateItem(index, "produtoDescricao", ""); }}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleValidateProdutoCod(index, item.produtoCod); } }}
                              onBlur={() => { if (item.produtoCod.trim() && item.codValido === null) handleValidateProdutoCod(index, item.produtoCod); }}
                              disabled={validatingIndex === index}
                              className={`h-7 text-xs ${item.codValido === false ? "border-red-500" : item.codValido === true ? "border-green-500" : ""}`}
                              placeholder={validatingIndex === index ? "Buscando..." : "SKU-001"}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input value={item.produtoDescricao} disabled className="h-7 text-xs bg-gray-50" placeholder="Automático" />
                          </td>
                          <td className="px-2 py-1">
                            <Input type="number" value={item.quantidade} onChange={(e) => updateItem(index, "quantidade", e.target.value)} min="1" className="h-7 text-xs" />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="checkbox"
                              checked={item.trocarEmbalagem}
                              onChange={(e) => updateItem(index, "trocarEmbalagem", e.target.checked)}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removeItem(index)} disabled={formItens.length === 1}>
                              <X className="w-3 h-3 text-red-500" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="w-3 h-3 mr-1" /> Adicionar Item
                </Button>
              </div>
            )}

            {modalAcao === "descarte" && (
              <p className="text-sm text-red-600">Este pedido será marcado como descartado. Esta ação não pode ser desfeita.</p>
            )}

            {modalAcao === "retirada" && (
              <p className="text-sm text-purple-600">Este pedido será marcado para retirada pelo emitente.</p>
            )}

            <div className="flex gap-2 mt-6 justify-end">
              <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleAcaoSubmit} disabled={acaoMutation.isPending}>
                {acaoMutation.isPending ? "Processando..." : "Confirmar"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Anotações */}
      {anotacaoModalOpen && anotacaoPedidoId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setAnotacaoModalOpen(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-bold">Anotações</h3>
              <button className="text-muted-foreground hover:text-foreground" onClick={() => setAnotacaoModalOpen(false)}>✕</button>
            </div>
            <p className="text-xs text-muted-foreground mb-4">Pedido {devolucoes?.find((d: any) => d.id === anotacaoPedidoId)?.codigoBip}. As anotações não podem ser editadas ou apagadas.</p>

            {anotacoes && anotacoes.length > 0 ? (
              <div className="space-y-2 mb-4 max-h-[300px] overflow-y-auto">
                {anotacoes.map((a: any) => (
                  <div key={a.id} className="border rounded p-2">
                    <p className="text-sm">{a.texto}</p>
                    <p className="text-xs text-muted-foreground">{a.criadoPor} • {formatDate(a.criadoEm)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mb-4">Nenhuma anotação ainda.</p>
            )}

            <textarea
              className="w-full border rounded p-2 text-sm resize-none"
              rows={3}
              maxLength={500}
              placeholder="Escreva uma anotação (máx. 500 caracteres)..."
              value={anotacaoTexto}
              onChange={(e) => setAnotacaoTexto(e.target.value)}
            />
            <p className="text-xs text-right text-muted-foreground mb-2">{anotacaoTexto.length}/500</p>
            <Button
              className="w-full"
              disabled={!anotacaoTexto.trim() || addAnotacaoMutation.isPending}
              onClick={() => addAnotacaoMutation.mutate({ pedidoId: anotacaoPedidoId, texto: anotacaoTexto.trim() })}
            >
              {addAnotacaoMutation.isPending ? "Salvando..." : "Adicionar anotação"}
            </Button>
          </div>
        </div>
      )}

      {/* Modal Log */}
      {logModalOpen && logPedidoId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setLogModalOpen(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">Log do Pedido</h3>
            {logEntries && logEntries.length > 0 ? (
              <div className="space-y-2">
                {logEntries.map((log: any) => (
                  <div key={log.id} className="border-b pb-2">
                    <p className="text-sm">{log.descricao}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(log.criadoEm)} - {log.criadoPor || "sistema"}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Nenhum registro no log.</p>
            )}
            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={() => setLogModalOpen(false)}>Fechar</Button>
            </div>
          </div>
        </div>
      )}

      {mlModalOpen && mlModalData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setMlModalOpen(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">Dados Mercado Livre</h3>
            {mlModalData.mlErro && (
              <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                <span className="font-semibold">Erro na sincronização:</span> {mlModalData.mlErro}
                {mlModalData.mlTentativas > 0 && <span className="ml-2">({mlModalData.mlTentativas} tentativa(s))</span>}
              </div>
            )}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Pedido ML</span>
                <span className="font-mono">{mlModalData.mlPedidoId ?? '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Pack ML</span>
                <span className="font-mono">{mlModalData.mlPackId ?? '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Envio Original</span>
                <span className="font-mono">{mlModalData.mlShippingIdPedido ?? '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">ID Devolução (Claim)</span>
                <span className="font-mono">{mlModalData.mlClaimId ?? '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Envio Devolução</span>
                <span className="font-mono">{mlModalData.mlShippingIdDevol ?? '-'}</span>
              </div>
              {mlModalData.mlItensPedido && (
                <div className="mt-3">
                  <p className="text-muted-foreground mb-1">Itens do Pedido</p>
                  <div className="space-y-1">
                    {(typeof mlModalData.mlItensPedido === 'string' ? JSON.parse(mlModalData.mlItensPedido) : mlModalData.mlItensPedido).map((item: any, i: number) => (
                      <div key={i} className="text-xs bg-muted rounded px-2 py-1">
                        <span className="font-medium">{item.item?.title ?? item.title ?? '-'}</span>
                        {item.quantity && <span className="ml-2 text-muted-foreground">x{item.quantity}</span>}
                        {item.unit_price && <span className="ml-2 text-muted-foreground">R$ {Number(item.unit_price).toFixed(2)}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={() => setMlModalOpen(false)}>Fechar</Button>
            </div>
          </div>
        </div>
      )}

      {jadlogModalOpen && jadlogModalData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setJadlogModalOpen(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">Dados Jadlog</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Código de rastreio</span>
                <span className="font-mono text-right break-all">{jadlogModalData.trackingJadlog ?? '-'}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">DACTE</span>
                <span className="font-mono text-right break-all">{jadlogModalData.dacteJadlog ?? '-'}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Nota fiscal processada</span>
                <span className="font-mono text-right break-all">{jadlogModalData.notaFiscalProcessadaJadlog ?? '-'}</span>
              </div>
              {jadlogModalData.destinatarioJadlog && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-muted-foreground">Destinatário</summary>
                  <pre className="mt-2 max-h-48 overflow-auto rounded bg-muted p-2 text-xs whitespace-pre-wrap break-all">{typeof jadlogModalData.destinatarioJadlog === 'string' ? jadlogModalData.destinatarioJadlog : JSON.stringify(jadlogModalData.destinatarioJadlog, null, 2)}</pre>
                </details>
              )}
              {jadlogModalData.xmlJadlog && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-muted-foreground">XML CT-e</summary>
                  <pre className="mt-2 max-h-48 overflow-auto rounded bg-muted p-2 text-xs whitespace-pre-wrap break-all">{jadlogModalData.xmlJadlog}</pre>
                </details>
              )}
            </div>
            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={() => setJadlogModalOpen(false)}>Fechar</Button>
            </div>
          </div>
        </div>
      )}

      {shopeeModalOpen && shopeeModalData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShopeeModalOpen(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">Dados Shopee</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Loja</span><span className="text-right break-all">{shopeeModalData.lojaShopee ?? '-'}</span></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Comprador</span><span className="text-right break-all">{shopeeModalData.compradorShopee ?? '-'}</span></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Pedido</span><span className="font-mono text-right break-all">{shopeeModalData.pedidoShopee ?? '-'}</span></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Rastreio original</span><span className="font-mono text-right break-all">{shopeeModalData.rastreioOriginalShopee ?? '-'}</span></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Data de envio</span><span className="text-right">{formatDate(shopeeModalData.dataEnvioShopee)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Data de devolução</span><span className="text-right">{formatDate(shopeeModalData.dataDevolucaoShopee)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Motivo</span><span className="text-right break-words">{shopeeModalData.motivoShopee ?? '-'}</span></div>
            </div>
            <div className="mt-4 flex justify-end"><Button variant="outline" onClick={() => setShopeeModalOpen(false)}>Fechar</Button></div>
          </div>
        </div>
      )}

      {fotoGuiadaOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={fecharFotoGuiada}>
          <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-1">Tirar Fotos de Devolução</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Etapa {fotoGuiadaEtapa} de 3 — {fotoGuiadaEtapa === 1 ? 'fotografe a etiqueta da devolução' : `fotografe o produto (${fotoGuiadaEtapa - 1} de 2)`}.
            </p>
            <div className="flex gap-1 mb-5">
              {[1, 2, 3].map((etapa) => (
                <div key={etapa} className={`h-2 flex-1 rounded ${etapa <= fotoGuiadaEtapa ? 'bg-blue-500' : 'bg-gray-200'}`} />
              ))}
            </div>
            <input
              ref={fotoGuiadaInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              disabled={fotoGuiadaLoading}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) await enviarFotoGuiada(file);
              }}
            />

            {fotoGuiadaEtapa === 1 ? (
              fotoGuiadaDevolucoesEncontradas.length > 1 ? (
                <div>
                  <p className="text-sm mb-3">Foram encontradas múltiplas devoluções. Selecione a correta:</p>
                  <div className="max-h-56 overflow-y-auto space-y-2">
                    {fotoGuiadaDevolucoesEncontradas.map((devolucao) => (
                      <Button
                        key={devolucao.codigoBip}
                        variant="outline"
                        className="w-full h-auto justify-start text-left py-2"
                        disabled={fotoGuiadaLoading}
                        onClick={() => salvarEtiquetaSelecionada(devolucao)}
                      >
                        <span>
                          <strong>{devolucao.codigoBip}</strong><br />
                          <span className="text-xs text-muted-foreground">{devolucao.marketplaceNome || 'Loja não informada'} — entrada: {formatDate(devolucao.bipEntradaEm)}</span>
                        </span>
                      </Button>
                    ))}
                  </div>
                  <Button className="w-full mt-3" variant="outline" disabled={fotoGuiadaLoading} onClick={() => fotoGuiadaInputRef.current?.click()}>
                    <Camera className="w-4 h-4 mr-2" />Tirar nova foto da etiqueta
                  </Button>
                </div>
              ) : (
                <Button className="w-full" disabled={fotoGuiadaLoading} onClick={() => fotoGuiadaInputRef.current?.click()}>
                  <Camera className="w-4 h-4 mr-2" />{fotoGuiadaLoading ? 'Validando etiqueta...' : 'Tirar Foto da Etiqueta'}
                </Button>
              )
            ) : (
              <div>
                <p className="text-sm mb-3">Devolução: <strong>{fotoGuiadaDevolucao?.codigoBip}</strong>{fotoGuiadaDevolucao?.marketplaceNome ? ` — ${fotoGuiadaDevolucao.marketplaceNome}` : ''}</p>
                <Button className="w-full" disabled={fotoGuiadaLoading} onClick={() => fotoGuiadaInputRef.current?.click()}>
                  <Camera className="w-4 h-4 mr-2" />{fotoGuiadaLoading ? 'Preparando e salvando foto...' : `Tirar Foto do Produto ${fotoGuiadaEtapa - 1} de 2`}
                </Button>
              </div>
            )}

            <div className="mt-5 flex justify-end">
              <Button variant="outline" disabled={fotoGuiadaLoading} onClick={fecharFotoGuiada}>Cancelar</Button>
            </div>
          </div>
        </div>
      )}

      {fotosModalOpen && fotosModalData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setFotosModalOpen(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl shadow-xl overflow-y-auto max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-1">Outros Arquivos</h3>
            <p className="text-sm text-muted-foreground mb-4">Pedido: <strong>{fotosModalData.cod}</strong></p>
            {['foto_etiqueta_devolucao','foto_etiqueta_produto','foto_produto_1','foto_produto_2','foto_produto_3'].some(c => fotosModalData[c]) && (
              <>
                <p className="text-xs font-semibold text-muted-foreground mb-2">Fonte externa</p>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {['foto_etiqueta_devolucao','foto_etiqueta_produto','foto_produto_1','foto_produto_2','foto_produto_3'].map((campo) =>
                    fotosModalData[campo] ? (
                      <a key={campo} href={`https://mapeamento.live${fotosModalData[campo]}`} target="_blank" rel="noopener noreferrer">
                        <img src={`https://mapeamento.live${fotosModalData[campo]}`} alt={campo} className="w-full h-32 object-cover rounded border hover:opacity-80" />
                        <p className="text-xs text-center text-muted-foreground mt-1">{campo.replace(/_/g,' ')}</p>
                      </a>
                    ) : null
                  )}
                </div>
              </>
            )}
            {fotosModalData._internas && fotosModalData._internas.length > 0 && (
              <>
                <p className="text-xs font-semibold text-muted-foreground mb-2">Fotos internas</p>
                <div className="grid grid-cols-3 gap-3">
                  {(fotosModalData._internas as string[]).map((url: string, i: number) => (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                      <img src={url} alt={`foto-interna-${i+1}`} className="w-full h-32 object-cover rounded border hover:opacity-80" />
                      <p className="text-xs text-center text-muted-foreground mt-1">foto {i+1}</p>
                    </a>
                  ))}
                </div>
              </>
            )}
            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={() => setFotosModalOpen(false)}>Fechar</Button>
            </div>
          </div>
        </div>
      )}

      {uploadFotosOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => !uploadFotosLoading && setUploadFotosOpen(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-1">Upload de Fotos Internas</h3>
            <p className="text-sm text-muted-foreground mb-4">Selecione até 50 imagens ou arquivos ZIP. Os códigos serão extraídos automaticamente do nome do arquivo, códigos de barras e OCR (8 ângulos). ZIPs serão descompactados automaticamente.</p>
            <input
              type="file"
              multiple
              accept="image/*,.zip,application/zip"
              className="block w-full text-sm mb-3"
              disabled={uploadFotosLoading}
              onChange={(e) => setUploadFotosFiles(Array.from(e.target.files || []).slice(0, 500))}
            />
            {uploadFotosFiles.length > 0 && (
              <p className="text-sm mb-3">{uploadFotosFiles.length} arquivo(s) selecionado(s)</p>
            )}
            {uploadFotosProgress && (
              <p className="text-sm text-blue-600 mb-3">{uploadFotosProgress}</p>
            )}
            {uploadFotosResultados.length > 0 && (
              <div className="max-h-48 overflow-y-auto text-xs border rounded p-2 mb-3">
                {uploadFotosResultados.map((r, i) => (
                  <div key={i} className={r.erro ? 'text-red-600' : 'text-green-700'}>
                    {r.arquivo}: {r.erro ? r.erro : `${(r.codigos || []).length} código(s) extraídos`}
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" disabled={uploadFotosLoading} onClick={() => setUploadFotosOpen(false)}>Fechar</Button>
              <Button
                disabled={uploadFotosLoading || uploadFotosFiles.length === 0}
                onClick={async () => {
                  if (uploadFotosFiles.length === 0) return;
                  setUploadFotosLoading(true);
                  setUploadFotosProgress('Enviando...');
                  setUploadFotosResultados([]);
                  try {
                    const formData = new FormData();
                    uploadFotosFiles.forEach(f => formData.append('files', f));
                    const res = await fetch('/api/devolucao-fotos/upload', { method: 'POST', body: formData, credentials: 'include' });
                    const data = await res.json();
                    if (!res.ok) { toast.error(data.error || 'Erro no upload'); }
                    else {
                      setUploadFotosResultados(data.resultados || []);
                      setUploadFotosProgress(`Concluído: ${data.processados} arquivo(s) processado(s)`);
                      toast.success(`${data.processados} foto(s) processada(s)`);
                    }
                  } catch (e: any) {
                    toast.error('Erro: ' + e.message);
                  } finally {
                    setUploadFotosLoading(false);
                  }
                }}
              >
                {uploadFotosLoading ? 'Processando...' : 'Enviar e Processar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
